import { forwardRef, useCallback } from "react";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { SnackbarProps, SnackbarVibe } from "./types";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardIconButton } from "@/dashboard-design-system/IconButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Error, Success as CheckCircle, Cross as Close, Info, Warning } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

const SnackbarIconVariant: Record<SnackbarVibe, IconElement> = {
  brand: <Info />,
  danger: <Error />,
  warning: <Warning />,
  positive: <CheckCircle />,
};

export const DashboardSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(function DashboardSnackbar(
  {
    buttonsProps = [],
    hideClose = false,
    hideIcon = false,
    icon,
    id,
    message,
    style,
    vibe = "brand",
    "data-test": dataTest = "snackbar",
  },
  ref
) {
  const theme = useTheme();
  const { closeSnackbar } = useSnackbar();

  const onClose = useCallback(() => {
    closeSnackbar(id);
  }, [closeSnackbar, id]);

  const surfaceVibe = theme.palette.surface[vibe].quiet;

  return (
    <Box
      ref={ref}
      sx={(theme) => ({
        borderRadius: theme.radius(0.5),
        minHeight: 54,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        // Necessary override needed because of the way notistack injects its styles
        flexWrap: "nowrap !important",
        padding: theme.spacing(1, 2),
        marginRight: theme.spacing(12),
        background: surfaceVibe,
        boxShadow: theme.boxShadow.base.mid,
      })}
      style={style}
      data-test={dataTest}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {!hideIcon && (icon || SnackbarIconVariant[vibe]) && (
          <DashboardIcon
            size="medium"
            vibe={`text-${vibe}-moderate`}
            sx={{ marginRight: theme.spacing(1) }}
            data-test="snackbar-icon"
          >
            {icon || SnackbarIconVariant[vibe]}
          </DashboardIcon>
        )}
        <DashboardTypography variant="body2" vibe={`text-${vibe}-moderate`} data-test="snackbar-message">
          {message}
        </DashboardTypography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {buttonsProps.map(({ key, ...restOfButtonProps }) => (
          <DashboardButton
            key={key}
            sx={{ marginLeft: theme.spacing(1) }}
            variant="tonal"
            vibe={vibe}
            {...restOfButtonProps}
          />
        ))}
        {!hideClose && (
          <DashboardIconButton
            sx={{ marginLeft: theme.spacing(1) }}
            size="medium"
            onClick={onClose}
            vibe={vibe}
            data-test="snackbar-dissmiss-button"
          >
            <Close />
          </DashboardIconButton>
        )}
      </Box>
    </Box>
  );
});
